import type { MutationKey, QueryKey } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { showToast } from "@/providers/ToastProvider";

type MutationContext<TCacheData> = { previous: TCacheData | undefined };

// TMutationResult defaults to TCacheData since most mutations return the same shape they cache
// (e.g. updateUserPermissions -> UserPermissions), but some return an unrelated ack/response
// shape (e.g. appendPatientConsent -> ConsentResponseDTO) while still caching PatientConsent[] —
// hence the separate type param.
type UseOptimisticMutationProps<TCacheData, TPayload, TMutationResult = TCacheData> = {
  getErrorMessage: (payload: TPayload) => string;
  // Set to `false` when the server needs time to settle (e.g. eventual consistency) — the
  // optimistic write then stays until the next natural refetch instead of being immediately
  // reconciled/reverted by an invalidation.
  invalidateOnSettled?: boolean;
  meta?: Record<string, unknown>;
  mutationFn: (payload: TPayload) => Promise<TMutationResult>;
  mutationKey?: MutationKey;
  // Opt-in — most optimistic mutations rely on the instant UI update and skip a success callback.
  onSuccess?: (data: TMutationResult, payload: TPayload) => void;
  optimisticStateResolver: (old: TCacheData, payload: TPayload) => TCacheData | undefined;
  queryKey: QueryKey;
};

// Generic building block for mutations that optimistically update a single query-cache entry.
// Not tied to any domain — supply `queryKey`/`mutationFn`/`optimisticStateResolver` per use case.
export function useOptimisticMutation<TCacheData, TPayload, TMutationResult = TCacheData>({
  getErrorMessage,
  invalidateOnSettled = true,
  meta,
  mutationFn,
  mutationKey,
  onSuccess,
  optimisticStateResolver,
  queryKey,
}: UseOptimisticMutationProps<TCacheData, TPayload, TMutationResult>) {
  const queryClient = useQueryClient();

  return useMutation<TMutationResult, unknown, TPayload, MutationContext<TCacheData>>({
    meta,
    mutationFn,
    mutationKey,
    onError: (_err, payload, context) => {
      if (context?.previous) {
        const { previous } = context;

        queryClient.setQueryData<TCacheData>(queryKey, previous);
      }

      showToast([{ heading: getErrorMessage(payload), variant: "error" }]);
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TCacheData>(queryKey);

      // If optimisticStateResolver returns undefined, this is a no-op write (falls back to
      // oldData) — no instant UI update. The real update arrives via onSettled's
      // invalidateQueries below, once the server response has the full picture.
      queryClient.setQueryData<TCacheData>(queryKey, (oldData) =>
        oldData ? (optimisticStateResolver(oldData, payload) ?? oldData) : oldData,
      );

      return { previous };
    },
    onSettled: () =>
      invalidateOnSettled ? queryClient.invalidateQueries({ queryKey }) : undefined,
    onSuccess: (data, payload) => onSuccess?.(data, payload),
  });
}
